import React from 'react';
import { View, StyleSheet, Platform, ViewStyle, StyleProp } from 'react-native';
import { CONTENT_MAX_WIDTH, DESKTOP_CONTENT_MAX_WIDTH } from '../../theme';
import { useDesktopShell } from '@hooks/useDesktopShell';
import type { WebViewStyle } from '@utils/webStyles';

interface ContentConstraintProps {
  children: React.ReactNode;
  maxWidth?: number;
  /**
   * Use the desktop-shell width (1120px) on >1024px web. Only for layouts
   * that actually use the width (grids, tables) — single reading columns
   * stay at 768 for line-length comfort.
   */
  wide?: boolean;
  style?: StyleProp<ViewStyle>;
}

/**
 * On web, caps children width (default 768px) and scrolls internally. On
 * native, passes through as a plain flex container. The parent must set
 * alignItems: 'center' for the cap to center on the page.
 */
export function ContentConstraint({
  children,
  maxWidth,
  wide = false,
  style,
}: ContentConstraintProps) {
  const isDesktopShell = useDesktopShell();
  const resolved =
    maxWidth ?? (wide && isDesktopShell ? DESKTOP_CONTENT_MAX_WIDTH : CONTENT_MAX_WIDTH);
  return <View style={[styles.constraint, { maxWidth: resolved }, style]}>{children}</View>;
}

const styles = StyleSheet.create({
  constraint: {
    flex: 1,
    width: '100%',
    ...Platform.select({
      web: { overflow: 'auto', scrollbarWidth: 'none' } as WebViewStyle,
      default: {},
    }),
  },
});
